import { Component, ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, Library, Plus } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

// NavigateButton component for reliable SPA navigation without nested interactive elements
function NavigateButton({ to, children, ...buttonProps }: { to: string; children: ReactNode } & any) {
  const handleClick = () => {
    try {
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', to);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        window.location.href = to;
      }
    } catch (error) {
      console.error('Navigation error:', error);
      window.location.href = to;
    }
  };

  return (
    <Button onClick={handleClick} {...buttonProps}>
      {children}
    </Button>
  );
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: any;
}

class StoryLibraryErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error("Story Library Error caught by boundary:", error, errorInfo);
    // Here you could send error to logging service
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    // Refresh the page to clear any corrupted state
    window.location.reload();
  };


  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full bg-white/90 backdrop-blur shadow-xl border-purple-200">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-blue-100 rounded-full">
                  <Library className="w-12 h-12 text-blue-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Story Library Temporarily Unavailable
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">
                We're having trouble loading your story library right now. This could be due to a temporary connection issue or a problem with our story service. Your stories are safe and we're working to restore access!
              </p>

              <div className="space-y-4 mb-6">
                <div className="text-sm text-gray-500">
                  <p><strong>What you can do:</strong></p>
                  <ul className="list-disc text-left max-w-md mx-auto mt-2 space-y-1">
                    <li>Try refreshing the page to reload your stories</li>
                    <li>Check your internet connection</li>
                    <li>Create a new story while we fix this issue</li>
                    <li>Try again in a few minutes</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.handleReset}
                  variant="gradient"
                  className="text-white"
                  data-testid="button-refresh-library"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Library
                </Button>
                <NavigateButton
                  to="/"
                  variant="outline"
                  data-testid="button-create-story"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Story
                </NavigateButton>
                <NavigateButton
                  to="/"
                  variant="outline"
                  data-testid="button-go-home-library"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </NavigateButton>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500">
                    Error details (Development)
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-100 p-4 rounded overflow-auto">
                    {this.state.error.toString()}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export the wrapper component for use in the app
export default function StoryLibraryErrorBoundary({ children, fallback }: Props) {
  return (
    <StoryLibraryErrorBoundaryClass fallback={fallback}>
      {children}
    </StoryLibraryErrorBoundaryClass>
  );
}